import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { blogAPI } from '../services/api';
import SEO from '../components/SEO';

const AdminBlog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    category: '',
    author: 'Équipe DEBECO'
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getAll();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (article) => {
    setIsEditing(true);
    setEditingArticle(article.id);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      image: article.image,
      category: article.category,
      author: article.author
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        await blogAPI.delete(id);
        setArticles(articles.filter(a => a.id !== id));
        toast.success('Article supprimé', {
          description: "L'article a été supprimé avec succès.",
        });
      } catch (error) {
        console.error('Error deleting article:', error);
        toast.error('Erreur', {
          description: "Impossible de supprimer l'article.",
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingArticle) {
        // Update existing
        const updated = await blogAPI.update(editingArticle, formData);
        setArticles(articles.map(a => a.id === editingArticle ? updated : a));
        toast.success('Article modifié', {
          description: "L'article a été mis à jour avec succès.",
        });
      } else {
        // Create new
        const newArticle = await blogAPI.create(formData);
        setArticles([newArticle, ...articles]);
        toast.success('Article créé', {
          description: "Le nouvel article a été publié avec succès.",
        });
      }
      handleCancel();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Erreur', {
        description: error.detail || "Impossible d'enregistrer l'article.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingArticle(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image: '',
      category: '',
      author: 'Équipe DEBECO'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title') {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8">
      <SEO 
        title="Administration Blog | DEBECO"
        description="Interface d'administration du blog DEBECO"
        noindex={true}
      />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue-900 mb-2">Gestion du Blog</h1>
          <p className="text-gray-600">Créez et modifiez les articles du blog</p>
        </div>

        {/* Add/Edit Form */}
        {isEditing && (
          <Card className="mb-8 border-2 border-emerald-500 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-blue-900">
                  {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                </h2>
                <Button variant="ghost" onClick={handleCancel}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="Titre de l'article"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug (URL)
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 bg-gray-50"
                    placeholder="slug-de-larticle"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Catégorie <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      required
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      <option value="Tarifs">Tarifs</option>
                      <option value="Recyclage">Recyclage</option>
                      <option value="Conseils">Conseils</option>
                      <option value="Succession">Succession</option>
                      <option value="Déménagement">Déménagement</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Auteur
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                      placeholder="Nom de l'auteur"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de l'image
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extrait <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="excerpt"
                    required
                    value={formData.excerpt}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 resize-none"
                    placeholder="Court résumé de l'article"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenu <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="content"
                    required
                    value={formData.content}
                    onChange={handleChange}
                    rows="10"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 resize-none"
                    placeholder="Contenu complet de l'article"
                  ></textarea>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingArticle ? 'Mettre à jour' : 'Publier'}
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCancel} disabled={submitting}>
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* New Article Button */}
        {!isEditing && (
          <div className="mb-8">
            <Button 
              onClick={() => setIsEditing(true)}
              className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel article
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-12 h-12 text-blue-900 animate-spin" />
          </div>
        )}

        {/* Articles List */}
        {!loading && articles.length === 0 && (
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">Aucun article pour le moment</p>
            </CardContent>
          </Card>
        )}

        {!loading && articles.length > 0 && (
          <div className="space-y-4">
            {articles.map((article) => (
            <Card key={article.id} className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Image */}
                  <div className="w-full md:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="192"
                      height="128"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
                            {article.category}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(article.date).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <h3 className="font-bold text-xl text-blue-900 mb-2">{article.title}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2">{article.excerpt}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(article)}
                          className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(article.id)}
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlog;